/**
 * Shared presentational timeline for history (semantic or full event list).
 * No Redux; caller controls selected index and syncs state.
 * Used by HistoryPanel (live) and ReplayHistoryPanel (replay).
 */

import {
  ArrowRightIcon,
  CounterClockwiseClockIcon,
  PersonIcon,
  PlayIcon,
} from '@radix-ui/react-icons';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import isNil from 'lodash/isNil';
import React, { useCallback, useMemo, useRef } from 'react';

import { HorseColorValues, useTranslation } from '~/app';
import calculateClientHorseColors from '~/game/calculateClientHorseColors';
import { buildSemanticSteps, getTurnInfoAtEventIndex } from '~/replay/eventCompression';
import { getEventLabel } from '~/replay/eventLabels';
import { computeStateAtEventIndex } from '~/replay/reducers';
import { EventLogEntry, EventType } from '~/server/src/dtos';
import { cn } from '~/utils/styles';

import { HistoryFullEventItem } from './HistoryFullEventItem';
import { HistoryStepItem, type StepItemMetadata } from './HistoryStepItem';
import { CheckeredFlagIcon, FlagIcon, MiniCardIcon, MiniDicePairIcon, TrophyIcon } from './icons';

/** Relative time for recent events. */
function formatRelativeTime(
  timestamp: number | Date,
  t: (key: string, opts?: { placeholders?: Record<string, string | number> }) => string,
): string {
  const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  if (diffMin < 1) return t('history_time_just_now');
  if (diffMin < 60) return t('history_time_min_ago', { placeholders: { count: diffMin } });
  if (diffHours < 24) return t('history_time_hours_ago', { placeholders: { count: diffHours } });
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Resolve horse index from move event payload (supports horseid, horseId, horseIndex). Returns null if invalid or out of bounds. */
export function resolveHorseIndexFromPayload(
  payload: unknown,
  horsesLength: number,
): number | null {
  if (!payload || typeof payload !== 'object' || horsesLength <= 0) return null;
  const p = payload as Record<string, unknown>;
  const raw =
    (p.horseid as number | undefined) ??
    (p.horseId as number | undefined) ??
    (p.horseIndex as number | undefined);
  if (isNil(raw)) return null;
  const idx = Number(raw);
  if (!Number.isFinite(idx) || idx < 0 || idx >= horsesLength) return null;
  return idx;
}

/** Keep move labels explicit about moved field count. */
export function getMoveLineLabel(
  label: string,
  step: {
    actionType?: 'card' | 'dice' | null;
    actionValue?: number | null;
    handicapExceeded?: boolean;
  },
  diceValuesForDisplay?: number[],
  t?: (key: string, opts?: { placeholders?: Record<string, string | number> }) => string,
): string {
  if (!t) return label;
  if (step.actionType === 'card' && step.actionValue !== null && step.actionValue !== undefined) {
    const isJoker = step.actionValue < 0;
    const displayValue = isJoker ? Math.abs(step.actionValue) : step.actionValue;
    if (isJoker) {
      return step.handicapExceeded
        ? t('event_first_joker_card_played_handicap', {
            placeholders: { total: displayValue, value: displayValue },
          })
        : t('event_first_joker_card_played', {
            placeholders: { total: displayValue, value: displayValue },
          });
    }
    return step.handicapExceeded
      ? t('event_first_card_played_handicap', {
          placeholders: { total: displayValue, value: displayValue },
        })
      : t('event_first_card_played', {
          placeholders: { total: displayValue, value: displayValue },
        });
  }
  if (step.actionType !== 'dice') return label;
  if (diceValuesForDisplay && diceValuesForDisplay.length > 0) {
    const total = diceValuesForDisplay.reduce((sum, val) => sum + val, 0);
    const isSingularField = total === 1;
    const isSingularDice = diceValuesForDisplay.length === 1;
    if (isSingularDice && isSingularField) {
      return t('event_first_moved_with_dice_singular', {
        placeholders: { total, value: diceValuesForDisplay[0] },
      });
    }
    if (isSingularDice) {
      return t('event_first_moved_with_dice', {
        placeholders: { total, value: diceValuesForDisplay[0] },
      });
    }
    if (isSingularField) {
      return t('event_first_moved_with_dice_multiple_singular', {
        placeholders: { total, values: diceValuesForDisplay.join(' + ') },
      });
    }
    return t('event_first_moved_with_dice_multiple', {
      placeholders: { total, values: diceValuesForDisplay.join(' + ') },
    });
  }

  if (typeof step.actionValue === 'number') {
    const total = step.actionValue;
    const isSingularField = total === 1;
    return isSingularField
      ? t('event_first_moved_with_dice_singular', {
          placeholders: { total, value: step.actionValue },
        })
      : t('event_first_moved_with_dice', {
          placeholders: { total, value: step.actionValue },
        });
  }

  return t('event_first_moved_with_dice_generic');
}

/** Flattened row for semantic list. */
type FlattenedRow =
  | { type: 'header'; roundNumber: number }
  | {
      type: 'turnHeader';
      turnNumber: number;
      turntype: 'card' | 'dice' | null;
      roundNumber: number;
    }
  | { type: 'step'; stepIndex: number; metadata: StepItemMetadata; roundNumber: number };

export type HistoryTimelineLiveButton = 'live' | 'end' | 'none';
export type HistorySelectionIntent =
  | { kind: 'live' }
  | { kind: 'semantic'; originalIndex: number }
  | { kind: 'index' };

export interface HistoryTimelineProps {
  eventLog: Array<{
    eventType: string;
    timestamp: number | Date | string;
    payload?: unknown;
    senderId?: string;
  }>;
  anchorState: Record<string, unknown> | null;
  /** Player list for resolving senderId → name (live: reducer Player[]; replay: full tmmodel Player[]). */
  players: Array<{ userId?: string; userid?: string; name?: string }>;
  /** Event index for displayed state (state *after* this index). null = live/end. */
  selectedEventIndex: number | null;
  onSelectedEventIndexChange: (index: number | null, intent?: HistorySelectionIntent) => void;
  onSpawnFromHere?: (eventIndex: number) => void;
  liveButton: HistoryTimelineLiveButton;
  viewMode: 'semantic' | 'full';
  /** When true, live button is active and no step row is "current". When false/undefined, inferred from selectedEventIndex for backward compat. */
  isLiveMode?: boolean;
  /** When true, step rows show more detail (e.g. two lines). */
  isExpanded?: boolean;
  /** Ref for scroll container (optional; component can use internal ref). */
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  /** Ref callback for step item buttons (semantic mode) for keyboard nav. */
  onStepRef?: (el: HTMLButtonElement | null, index: number) => void;
}

const ROUND_HEADER_HEIGHT = 34;
const TURN_HEADER_HEIGHT = 34;

/** Normalize props eventLog to EventLogEntry[] for replay helpers (senderId required). */
function normalizeEventLog(
  raw: Array<{
    eventType: string;
    timestamp: number | Date | string;
    payload?: unknown;
    senderId?: string;
  }>,
): EventLogEntry[] {
  return raw.map((e) => {
    const ts = e.timestamp;
    const iso =
      typeof ts === 'number'
        ? new Date(ts).toISOString()
        : typeof ts === 'string'
          ? ts
          : (ts as Date).toISOString();
    return {
      eventType: e.eventType as EventType,
      timestamp: iso,
      payload: e.payload ?? {},
      senderId: e.senderId ?? 'SERVER',
    };
  });
}

export const HistoryTimeline: React.FC<HistoryTimelineProps> = ({
  eventLog,
  anchorState,
  players,
  selectedEventIndex,
  onSelectedEventIndexChange,
  onSpawnFromHere,
  liveButton,
  viewMode,
  isLiveMode: isLiveModeProp,
  isExpanded = true,
  scrollContainerRef: externalScrollRef,
  onStepRef,
}) => {
  const { t } = useTranslation();
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = externalScrollRef ?? internalScrollRef;

  const isLive =
    isLiveModeProp !== undefined
      ? isLiveModeProp
      : selectedEventIndex === null || selectedEventIndex === eventLog.length;

  const getSemanticStepIcon = useCallback((eventType: EventType): React.ReactNode => {
    switch (eventType) {
      case EventType.JOINGAMEOPP:
        return <PersonIcon className="h-3.5 w-3.5" />;
      case EventType.LEAVEGAMEOPP:
        return <PersonIcon className="h-3.5 w-3.5" />;
      case EventType.REJOINGAME:
      case EventType.TM_REJOINGAME:
        return <ArrowRightIcon className="h-3.5 w-3.5" />;
      case EventType.STARTROUND:
        return <FlagIcon className="h-3.5 w-3.5" />;
      case EventType.ENDROUND:
        return <CheckeredFlagIcon className="h-3.5 w-3.5" />;
      case EventType.ENDGAME:
        return <TrophyIcon className="h-3.5 w-3.5" />;
      case EventType.ADDBONUSCARDS:
      case EventType.ADDBONUSCARDSOPP:
        return <MiniCardIcon value={0} color="#b45309" className="h-3.5 w-3.5" />;
      default:
        return <PersonIcon className="h-3.5 w-3.5" />;
    }
  }, []);

  const normalizedEventLog = useMemo(() => normalizeEventLog(eventLog), [eventLog]);
  const semanticSteps = useMemo(() => buildSemanticSteps(normalizedEventLog), [normalizedEventLog]);

  const stepMetadata = useMemo((): StepItemMetadata[] => {
    if (!anchorState || semanticSteps.length === 0) return [];
    const reversedSteps = [...semanticSteps].reverse();
    return reversedSteps.map((step) => {
      const stepState = computeStateAtEventIndex(
        anchorState,
        normalizedEventLog,
        step.targetEventIndex,
      );
      const stateBeforeEvent =
        normalizedEventLog.length > 0
          ? computeStateAtEventIndex(anchorState, normalizedEventLog, step.originalIndex)
          : null;
      const statePlayersBefore = stateBeforeEvent?.players as
        | Array<{ userid?: string; userId?: string; name?: string }>
        | undefined;
      const turnorder = stateBeforeEvent?.turnorder as number[] | undefined;
      const turnpos =
        typeof stateBeforeEvent?.turnpos === 'number' ? stateBeforeEvent.turnpos : undefined;
      const horsesBefore = stateBeforeEvent?.horses as Array<{ playerid?: number }> | undefined;
      const horseIdx = turnorder && turnpos !== undefined ? turnorder[turnpos] : undefined;
      const statePlayerIndexFromTurn =
        typeof horseIdx === 'number' && horsesBefore?.[horseIdx]?.playerid !== undefined
          ? (horsesBefore[horseIdx].playerid as number)
          : undefined;
      const label = getEventLabel(
        step.entry,
        (key, opts) => t(key, opts),
        players,
        stepState?.horses,
        statePlayersBefore ?? (stepState?.players as typeof statePlayersBefore),
        statePlayerIndexFromTurn,
      );
      const stepIcon = getSemanticStepIcon(step.entry.eventType);
      const timeLabel = formatRelativeTime(
        typeof step.entry.timestamp === 'number'
          ? step.entry.timestamp
          : new Date(step.entry.timestamp),
        t,
      );
      let horseColorHex: string | null = null;
      let horseName: string | null = null;
      if (
        step.entry.eventType === EventType.SELECTMOVEOPP &&
        stepState?.horses &&
        stepState?.players
      ) {
        const horsesLength = stepState.horses?.length ?? 0;
        const hidx = resolveHorseIndexFromPayload(step.entry.payload, horsesLength);
        if (hidx !== null) {
          const clientHorseColors = calculateClientHorseColors(
            stepState.horses ?? [],
            stepState.players ?? [],
            0,
          );
          const colorIndex = clientHorseColors[hidx];
          if (colorIndex !== undefined && colorIndex >= 0 && colorIndex < 8) {
            horseColorHex = HorseColorValues[colorIndex];
          }
          const horse = (stepState.horses as Array<{ name?: string }>)[hidx];
          horseName = horse?.name?.trim() || t('history_unknown_horse');
        }
      }
      const isPlayerScopedEvent =
        step.entry.eventType === EventType.SELECTCARDDICE ||
        step.entry.eventType === EventType.SELECTDICES ||
        step.entry.eventType === EventType.SELECTDICESOPP ||
        step.entry.eventType === EventType.SELECTMOVEOPP ||
        step.entry.eventType === EventType.ADDBONUSCARDSOPP ||
        step.entry.eventType === EventType.SKIPPLAYER;
      const player: (typeof players)[number] | null =
        isPlayerScopedEvent && step.entry.senderId
          ? (players.find((p) => (p.userid ?? p.userId) === step.entry.senderId) ?? null)
          : null;
      const isSystemEvent =
        step.entry.eventType === EventType.STARTROUND ||
        step.entry.eventType === EventType.ENDROUND ||
        step.entry.eventType === EventType.ENDGAME ||
        step.entry.eventType === EventType.ADDBONUSCARDS;
      const diceValuesForDisplay =
        step.entry.eventType === EventType.SELECTMOVEOPP &&
        step.actionType === 'dice' &&
        step.diceValues?.length
          ? step.diceValues
          : undefined;
      const line1 =
        step.entry.eventType === EventType.SELECTMOVEOPP
          ? getMoveLineLabel(label, step, diceValuesForDisplay, (key, opts) => t(key, opts))
          : label;
      const line2 = isSystemEvent ? '' : (player?.name ?? '');
      const playerIdForDisplay = player ? (player.userid ?? player.userId ?? '') : '';
      let diceRollPlayerColorHex: string | null = null;
      if (
        (step.entry.eventType === EventType.SELECTDICES ||
          step.entry.eventType === EventType.SELECTDICESOPP ||
          step.entry.eventType === EventType.ADDBONUSCARDSOPP ||
          step.entry.eventType === EventType.SKIPPLAYER) &&
        player &&
        stepState?.horses &&
        stepState?.players
      ) {
        const playerId = player.userid ?? player.userId;
        const playerIndex = (
          stepState.players as Array<{ userid?: string; userId?: string }>
        ).findIndex((p) => (p.userid ?? p.userId) === playerId);
        if (playerIndex >= 0) {
          const playerHorse = (stepState.horses as Array<{ playerid?: number }>).find(
            (h) => h.playerid === playerIndex,
          );
          if (playerHorse) {
            const horseIndex = (stepState.horses as Array<{ playerid?: number }>).indexOf(
              playerHorse,
            );
            const clientHorseColors = calculateClientHorseColors(
              stepState.horses ?? [],
              stepState.players ?? [],
              0,
            );
            const colorIndex = clientHorseColors[horseIndex];
            if (colorIndex !== undefined && colorIndex >= 0 && colorIndex < 8) {
              diceRollPlayerColorHex = HorseColorValues[colorIndex];
            }
          }
        }
      }
      const ariaParts = [line1];
      if (player?.name) ariaParts.push(player.name);
      if (horseName) ariaParts.push(horseName);
      ariaParts.push(timeLabel);
      const ariaLabel = ariaParts.filter(Boolean).join(', ');
      return {
        step,
        isActive: false,
        isLive: false,
        line1,
        line2,
        timeLabel,
        ariaLabel,
        stepIcon,
        horseColorHex,
        horseName,
        diceRollPlayerColorHex,
        playerIdForDisplay,
        player,
        diceValuesForDisplay,
      };
    });
  }, [anchorState, normalizedEventLog, semanticSteps, players, t, getSemanticStepIcon]);

  const roundByChronologicalIndex = useMemo(() => {
    let currentRound = 0;
    const rounds: number[] = [];
    for (const step of semanticSteps) {
      if (step.entry.eventType === EventType.STARTROUND) currentRound++;
      rounds.push(currentRound);
    }
    return rounds;
  }, [semanticSteps]);

  const turnByChronologicalIndex = useMemo(
    () =>
      semanticSteps.map((step) => getTurnInfoAtEventIndex(normalizedEventLog, step.originalIndex)),
    [semanticSteps, normalizedEventLog],
  );

  const flattenedList = useMemo((): FlattenedRow[] => {
    if (stepMetadata.length === 0) return [];
    const list: FlattenedRow[] = [];
    let previousRound: number | null = null;
    let previousTurn: number | null = null;
    for (let stepIndex = 0; stepIndex < stepMetadata.length; stepIndex++) {
      const chronologicalIndex = semanticSteps.length - 1 - stepIndex;
      const round = roundByChronologicalIndex[chronologicalIndex] ?? 0;
      const { turnNumber, turntype } = turnByChronologicalIndex[chronologicalIndex] ?? {
        turnNumber: 0,
        turntype: null as 'card' | 'dice' | null,
      };
      // Only emit a header for setup (round 0); no separate round headers for rounds >= 1
      if (round === 0 && (previousRound === null || previousRound !== 0)) {
        list.push({ type: 'header', roundNumber: 0 });
      }
      previousRound = round;
      if (previousTurn === null || turnNumber !== previousTurn) {
        if (turnNumber > 0) {
          list.push({ type: 'turnHeader', turnNumber, turntype, roundNumber: round });
        }
        previousTurn = turnNumber;
      }
      list.push({ type: 'step', stepIndex, metadata: stepMetadata[stepIndex], roundNumber: round });
    }
    return list;
  }, [stepMetadata, semanticSteps, roundByChronologicalIndex, turnByChronologicalIndex]);

  const count = viewMode === 'semantic' ? flattenedList.length : eventLog.length;

  const getItemKey = useCallback(
    (index: number): number | string => {
      if (viewMode === 'full') return index;
      const row = flattenedList[index];
      if (!row) return index;
      if (row.type === 'header') return `header-${row.roundNumber}`;
      if (row.type === 'turnHeader') return `turn-${row.turnNumber}-${row.turntype}`;
      return row.metadata.step.originalIndex;
    },
    [viewMode, flattenedList],
  );

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual required for virtualized list
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollRef.current,
    getItemKey,
    estimateSize: (index) => {
      if (viewMode === 'full') return 64;
      const row = flattenedList[index];
      if (!row) return isExpanded ? 56 : 44;
      if (row.type === 'header') return ROUND_HEADER_HEIGHT;
      if (row.type === 'turnHeader') return TURN_HEADER_HEIGHT;
      const hasSecondLine =
        row.type === 'step' &&
        (!!row.metadata.line2 || !!(row.metadata.horseName && row.metadata.horseColorHex));
      const hasSpawn = row.type === 'step' && !!onSpawnFromHere;
      const extra = hasSpawn ? 28 : 0;
      if (isExpanded) return (hasSecondLine ? 56 : 48) + extra;
      return (hasSecondLine ? 50 : 40) + extra;
    },
    overscan: 5,
    rangeExtractor: defaultRangeExtractor,
  });

  const handleStepBack = useCallback(() => {
    if (viewMode === 'full') {
      if (selectedEventIndex === null || selectedEventIndex === 0) return;
      onSelectedEventIndexChange(selectedEventIndex - 1, { kind: 'index' });
    } else {
      if (semanticSteps.length === 0) return;
      if (isLive) {
        const lastStep = semanticSteps[semanticSteps.length - 1];
        onSelectedEventIndexChange(lastStep.targetEventIndex, {
          kind: 'semantic',
          originalIndex: lastStep.originalIndex,
        });
      } else {
        const idx = semanticSteps.findIndex((s) => s.targetEventIndex >= (selectedEventIndex ?? 0));
        const stepIndex = idx < 0 ? semanticSteps.length - 1 : idx === 0 ? -1 : idx - 1;
        if (stepIndex >= 0) {
          onSelectedEventIndexChange(semanticSteps[stepIndex].targetEventIndex, {
            kind: 'semantic',
            originalIndex: semanticSteps[stepIndex].originalIndex,
          });
        }
      }
    }
  }, [viewMode, semanticSteps, selectedEventIndex, isLive, onSelectedEventIndexChange]);

  const handleStepForward = useCallback(() => {
    if (viewMode === 'full') {
      if (selectedEventIndex === null) return;
      if (selectedEventIndex >= eventLog.length) return;
      onSelectedEventIndexChange(Math.min(selectedEventIndex + 1, eventLog.length), {
        kind: 'index',
      });
    } else {
      if (selectedEventIndex === null) return;
      const idx = semanticSteps.findIndex((s) => s.targetEventIndex === selectedEventIndex);
      if (idx < semanticSteps.length - 1) {
        onSelectedEventIndexChange(semanticSteps[idx + 1].targetEventIndex, {
          kind: 'semantic',
          originalIndex: semanticSteps[idx + 1].originalIndex,
        });
      } else {
        onSelectedEventIndexChange(eventLog.length, { kind: 'live' });
      }
    }
  }, [viewMode, semanticSteps, selectedEventIndex, eventLog.length, onSelectedEventIndexChange]);

  const canStepBack =
    viewMode === 'full'
      ? selectedEventIndex !== null && selectedEventIndex > 0
      : semanticSteps.length > 0 &&
        (isLive || semanticSteps[0].targetEventIndex < (selectedEventIndex ?? 0));
  const canStepForward =
    viewMode === 'full'
      ? selectedEventIndex !== null && selectedEventIndex < eventLog.length
      : !isLive;

  const handleLiveOrEndClick = useCallback(() => {
    onSelectedEventIndexChange(eventLog.length, { kind: 'live' });
  }, [eventLog.length, onSelectedEventIndexChange]);

  return (
    <>
      <div className="relative mb-2 flex flex-shrink-0 items-center justify-between py-1">
        <button
          type="button"
          onClick={handleStepBack}
          disabled={!canStepBack}
          className={cn(
            'relative z-10 flex items-center gap-1.5 rounded-full p-1.5 text-amber-900 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50',
          )}
          aria-label={t('replay_step_back')}
        >
          <CounterClockwiseClockIcon className="h-4 w-4 shrink-0" />
          {isExpanded && (
            <span className="text-xs font-medium whitespace-nowrap">{t('replay_step_back')}</span>
          )}
        </button>
        <h3 className="pointer-events-none absolute right-0 left-0 flex justify-center text-sm font-semibold text-amber-900">
          {t('replay_history')}
        </h3>
        <button
          type="button"
          onClick={handleStepForward}
          disabled={!canStepForward}
          className={cn(
            'relative z-10 flex items-center gap-1.5 rounded-full p-1.5 text-amber-900 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50',
          )}
          aria-label={t('replay_step_forward')}
        >
          {isExpanded && (
            <span className="text-xs font-medium whitespace-nowrap">
              {t('replay_step_forward')}
            </span>
          )}
          <CounterClockwiseClockIcon className="h-4 w-4 shrink-0 -scale-x-100" />
        </button>
      </div>

      {(liveButton === 'live' || liveButton === 'end') && (
        <div className="flex-shrink-0 pr-4">
          <button
            ref={onStepRef ? (el) => onStepRef(el as unknown as HTMLButtonElement, 0) : undefined}
            type="button"
            onClick={handleLiveOrEndClick}
            className={cn(
              'flex w-full cursor-pointer items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-50/90 p-1.5 text-left transition-colors',
              isLive
                ? 'bg-amber-500/20 outline-1 outline-offset-[-1px] outline-amber-500/50'
                : 'hover:bg-amber-400/20',
            )}
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/20 text-primary">
              <PlayIcon className="h-4 w-4" />
            </div>
            <span className="min-w-0 truncate text-xs font-medium whitespace-nowrap text-amber-800">
              {liveButton === 'live' ? t('history_go_back_to_live') : t('history_go_to_end')}
            </span>
          </button>
        </div>
      )}

      <div
        ref={scrollRef as React.RefObject<HTMLDivElement>}
        className="relative min-h-0 flex-1 overflow-y-auto pr-4"
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            if (viewMode === 'full') {
              const index = virtualItem.index;
              const entry = normalizedEventLog[index];
              if (!entry) return null;
              const isActive = selectedEventIndex !== null && selectedEventIndex === index + 1;
              return (
                <div
                  key={virtualItem.key}
                  ref={virtualizer.measureElement}
                  data-index={virtualItem.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <HistoryFullEventItem
                    entry={entry}
                    index={index}
                    isActive={isActive}
                    onSelect={() => onSelectedEventIndexChange(index + 1, { kind: 'index' })}
                    onSpawnFromHere={onSpawnFromHere ? () => onSpawnFromHere(index + 1) : undefined}
                    players={players}
                  />
                </div>
              );
            }

            const row = flattenedList[virtualItem.index];
            if (!row) return null;

            const rowContent =
              row.type === 'header' ? (
                <div className="flex items-center border-b border-amber-900/70 px-2 py-1 text-xs font-medium tracking-wide text-amber-900 uppercase">
                  {t('history_section_setup')}
                </div>
              ) : row.type === 'turnHeader' ? (
                <div className="flex items-center gap-1.5 border-b border-amber-900/60 px-2 pt-1 pb-0.5 text-xs font-medium tracking-wide text-amber-800 uppercase">
                  {row.turntype === 'card' && (
                    <MiniCardIcon
                      value={null}
                      color="#b45309"
                      className="h-3.5 w-3.5 shrink-0"
                      empty
                    />
                  )}
                  {row.turntype === 'dice' && (
                    <MiniDicePairIcon color="#b45309" className="h-3.5 w-3.5 shrink-0" />
                  )}
                  <span className="min-w-0 truncate">
                    {row.turntype === 'card'
                      ? t('history_turn_title_cards', {
                          placeholders: { round: row.roundNumber, turn: row.turnNumber },
                        })
                      : row.turntype === 'dice'
                        ? t('history_turn_title_dice', {
                            placeholders: { round: row.roundNumber, turn: row.turnNumber },
                          })
                        : t('history_turn_title', {
                            placeholders: { round: row.roundNumber, turn: row.turnNumber },
                          })}
                  </span>
                </div>
              ) : (
                <HistoryStepItem
                  metadata={{
                    ...row.metadata,
                    isActive:
                      isLiveModeProp !== true &&
                      row.metadata.step.targetEventIndex === selectedEventIndex,
                  }}
                  compact={!isExpanded}
                  itemIndex={row.stepIndex + 1}
                  onRef={onStepRef ?? (() => {})}
                  onSelectStep={(step) =>
                    onSelectedEventIndexChange(step.targetEventIndex, {
                      kind: 'semantic',
                      originalIndex: step.originalIndex,
                    })
                  }
                  onSpawnFromHere={onSpawnFromHere}
                />
              );

            return (
              <div
                key={virtualItem.key}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {rowContent}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
